import dayjs from "dayjs";

import { SelectOptions } from "../../assets/data/options";
import { ApproveUserInput } from "../../backend/generated/approve-user-input";
import { CreateUserInput } from "../../backend/generated/create-user-input";
import { Experience } from "../../backend/generated/experience-model";
import { GetIdNowUserInput } from "../../backend/generated/get-id-now-user-input";
import { LoginUserInput } from "../../backend/generated/login-user-input";
import { RegisterCompanyInput } from "../../backend/generated/register-company-input";
import { RegisterUserInput } from "../../backend/generated/register-user-input";
import { UpdateUserInput } from "../../backend/generated/update-user-input";
import { User } from "../../backend/generated/user-model";
import { EducationLevel, ExperienceType, Gender, IdNowStatus, IdTypes, OccupationGroup, ValidationMessages } from "../../backend/types/utils";
import { randomAdjective, randomAutocompleteOption, randomBoolean, randomDate, randomNoun, randomNumber, randomSelectOption, randomString } from "../../utils/random";
import { extract, prune } from "../../utils/utility";
import { CustomValidationArguments, validateBooleansTrue, validateCellphones, validateDates, validateRequiredNotDefault } from "../../utils/validation";

export enum AccountType {
  Private = 0,
  Company = 1,
}

export class UserManageable extends User {
  constructor(obj: any) {
    super();

    obj && Object.assign(
      this,
      prune(obj),
      {
        defaultPayment: obj.defaultPayment ? obj.defaultPayment : '',
        gender: obj.gender ? obj.gender.toLowerCase() as Gender : obj.gender,
        highestEducation: obj.highestEducation ? obj.highestEducation.toLowerCase() as EducationLevel : obj.highestEducation,
        idDocumentType: obj.idDocumentType ? obj.idDocumentType.toLowerCase() as IdTypes : obj.idDocumentType,
        idNowStatus: obj.idNowStatus ? obj.idNowStatus.toLowerCase() as IdNowStatus : obj.idNowStatus,
        occupationGroup: obj.occupationGroup ? obj.occupationGroup.toLowerCase() as OccupationGroup : obj.occupationGroup,
        experience: obj.experience ? obj.experience.map((experience: Experience) => Object.assign(
          experience,
          {
            type: experience.type.toLowerCase(),
            level: experience.level.toLowerCase(),
          }
        )) : obj.experience,
      }
    );

    const payments = [];
    if (this.payments) {
      for (let i = 0; i < this.payments.length; i++) {
        payments.push(this.payments[i]);
      }
    }
    this.payments = payments;

    const investments = [];
    if (this.investments) {
      for (let i = 0; i < this.investments.length; i++) {
        investments.push(this.investments[i]);
      }
    }
    this.investments = investments;
  }

  static default(): any {
    return {
      ...new User(),
      idNowStatus: null,
    }
  }

  static hasExperience(
    input: any, type: ExperienceType
  ) {
    const noExperience = SelectOptions().experience[0].value;
    const defaultExperience = "";
    const hasXp = (xp: any) => xp !== noExperience && xp !== defaultExperience;
    const experience = input.experience.find((experience: Experience) => experience.type === type);

    return (
      !input.notProvidingExperience &&
      experience !== undefined &&
      hasXp(experience.level)
    );
  }

  hasExperience(type: ExperienceType) {
    return UserManageable.hasExperience(
      this,
      type
    );
  }

  userKnown(): boolean {
    return this.idNowStatus !== UserManageable.default().idNowStatus;
  }

  userVerified(): boolean {
    return this.idNowStatus === IdNowStatus.SUCCESS;
  }
}

export class ApproveUserManageable extends ApproveUserInput {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        ApproveUserManageable.default(),
        prune(extract(
          obj,
          Object.keys(this)
        ))
      );
    } else {
      Object.assign(
        this,
        ApproveUserManageable.default()
      );
    }
  }

  static required(): any {
    return { };
  }

  static default(): any {
    return { ...new ApproveUserInput() };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    return validateRequiredNotDefault(
      {},
      ApproveUserManageable.required(),
      this,
      ApproveUserManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    data.gender = data.gender.toUpperCase();

    return data;
  }
}

export class CreateUserManageable extends CreateUserInput {
  notProvidingExperience?: boolean;

  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        CreateUserManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        CreateUserManageable.default()
      );
    }
  }

  hasExperience(type: ExperienceType) {
    return UserManageable.hasExperience(
      this,
      type
    );
  }

  required(): any {
    return {
      firstName: true,
      lastName: true,
      cellphone: true,
      birthDate: true,
      birthCity: true,
      gender: true,
      citizenship: true,
      taxNumber: true,
      notUSTax: true,
      politicallyExposed: true,
      idDocumentType: true,
      idDocumentNumber: true,
      idDocumentCountry: true,
      passportIssuingAgency: true,
      address: true,
      addressCity: true,
      addressZipcode: true,
      addressCountry: true,
      employment: true,
      prevEmployment: true,
      highestEducation: true,
      occupationGroup: true,
      experience: Object.values(ExperienceType).map((x) => ({
        level: !this.notProvidingExperience,
        type: true,
        transactionsPerYear: this.hasExperience(x),
        volumePerYear: this.hasExperience(x),
      })),
    };
  }

  static default(): any {
    return {
      ...new CreateUserInput(),
      birthDate: "",
      gender: "",
      citizenship: "",
      politicallyExposed: "",
      idDocumentType: "",
      idDocumentCountry: "",
      addressCountry: "",
      highestEducation: null,
      occupationGroup: null,
      experience: Object.values(ExperienceType).map((type: string) => Object.assign(
        new Experience(),
        { type }
      )),
      notProvidingExperience: false,
    };
  }

  static automatic(): any {
    const notProvidingExperience = randomBoolean();
    const noExperience = SelectOptions().experience[0].value;

    return {
      ...CreateUserManageable.default(),
      firstName: randomAdjective(),
      lastName: randomNoun(),
      cellphone: "+49232323543",
      birthDate: randomDate(),
      birthCity: "X",
      gender: randomSelectOption('gender'),
      citizenship: randomAutocompleteOption('countries'),
      taxNumber: "X",
      notUSTax: true,
      politicallyExposed: randomSelectOption('yesNo'),
      idDocumentType: randomSelectOption('idDocumentType'),
      idDocumentNumber: randomString(6),
      idDocumentCountry: randomAutocompleteOption('countries'),
      passportIssuingAgency: "X",
      address: "X",
      addressCity: "X",
      addressZipcode: "X",
      addressCountry: randomAutocompleteOption('countries'),
      employment: "X",
      prevEmployment: "X",
      highestEducation: randomAutocompleteOption('educationLevel'),
      occupationGroup: randomAutocompleteOption('occupationGroup'),
      notProvidingExperience: notProvidingExperience,
      experience: Object.values(ExperienceType).map((type: string) => Object.assign(
        new Experience(),
        {
          type,
          level: notProvidingExperience ? noExperience : randomSelectOption('experience'),
          transactionsPerYear: notProvidingExperience ? 0 : randomNumber(
            0,
            1000
          ),
          volumePerYear: notProvidingExperience ? 0 : randomNumber(
            0,
            1000
          ),
        }
      )),
    };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    const shouldValidate = (key: string): boolean => {
      return keys.includes(key);
    };

    let errors: any = {};

    errors = Object.assign(
      errors,
      validateCellphones(new CustomValidationArguments({
        keys: ['cellphone'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.INVALID]
      }))
    );

    errors = Object.assign(
      errors,
      validateBooleansTrue(new CustomValidationArguments({
        keys: ['notUSTax'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.REQUIRED]
      }))
    );

    errors = Object.assign(
      errors,
      validateDates(new CustomValidationArguments({
        keys: ['birthDate'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.REQUIRED]
      }))
    );

    validateRequiredNotDefault(
      errors,
      this.required(),
      this,
      {
        ...CreateUserManageable.default(), // Use defaults from CreateUserManageable since defaults from UpdateUserManageable are empty for the purpose of disabling non-editable fields
        experience: Object.values(ExperienceType).map(() => ({ })),
      },
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    if (!errors.birthDate && shouldValidate("birthDate")) {
      let maxBirthDate = new Date();
      maxBirthDate.setFullYear(maxBirthDate.getFullYear() - 18);
      if (dayjs(this.birthDate).isAfter(maxBirthDate)) {
        errors.birthDate = messages[ValidationMessages.INVALID];
      }
    }

    return errors;
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    if (dayjs(data.birthDate).isValid()) {
      data.birthDate = dayjs(data.birthDate).toISOString();
    }

    data.gender = data.gender.toUpperCase();
    data.highestEducation = data.highestEducation.toUpperCase();
    data.idDocumentType = data.idDocumentType.toUpperCase();
    data.occupationGroup = data.occupationGroup.toUpperCase();

    if (this.notProvidingExperience) {
      const noExperience = SelectOptions().experience[0].value;
      data.experience = Object.values(ExperienceType).map((type: string) => Object.assign(
        new Experience(),
        {
          type: type.toUpperCase(),
          level: noExperience.toUpperCase(),
        }
      ));
    } else {
      data.experience = data.experience.map((experience: Experience) => Object.assign(
        experience,
        {
          type: experience.type.toUpperCase(),
          level: experience.level.toUpperCase(),
        }
      ));
    }

    delete data.notProvidingExperience;

    return data;
  }
}

export class GetIdNowUserManageable extends GetIdNowUserInput {
  constructor(obj?: any) {
    super();

    if (obj) {
      obj && Object.assign(
        this,
        GetIdNowUserManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        GetIdNowUserManageable.default()
      );
    }
  }

  static required(): any {
    return { idNowTransactionId: true };
  }

  static default(): any {
    return { ...new GetIdNowUserInput() };
  }

  serialize(): any {
    return this;
  }
}

export class LoginUserManageable extends LoginUserInput {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        LoginUserManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        LoginUserManageable.default()
      );
    }
  }

  static required(): any {
    return {
      email: true,
      password: true,
    };
  }

  static default(): any {
    return { ...new LoginUserInput() };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      LoginUserManageable.required(),
      this,
      LoginUserManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    return errors;
  }

  serialize(): any {
    return this;
  }
}

export class RegisterUserManageable extends RegisterUserInput {
  accountType?: AccountType;
  agreementFinpro?: boolean;
  confirmPassword?: string;

  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        RegisterUserManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        RegisterUserManageable.default()
      );
    }
  }

  required(): any {
    return {
      agreementFinpro: true,
      company: this.accountType === AccountType.Company ? {
        cocNumber: true,
        companyName: true,
      } : false,
      confirmPassword: true,
      email: true,
      password: true,
    };
  }

  static default(): any {
    return {
      ...new RegisterUserInput(),
      accountType: AccountType.Private,
      agreementFinpro: false,
      confirmPassword: "",
      company: new RegisterCompanyInput(),
    };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      this.required(),
      this,
      RegisterUserManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    if (!errors.password && this.password !== this.confirmPassword) {
      errors.confirmPassword = messages[ValidationMessages.NOTMATCHING];
    }

    if (!errors.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(this.email)) {
      errors.email = messages[ValidationMessages.INVALID];
    }

    return errors;
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    if (data.accountType === AccountType.Private) {
      delete data.company;
    }

    delete data.accountType;
    delete data.agreementFinpro;
    delete data.confirmPassword;

    return data;
  }
}

export class UpdateUserManageable extends UpdateUserInput {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        UpdateUserManageable.default(),
        prune(extract(
          obj,
          Object.keys(this)
        ))
      );
    } else {
      Object.assign(
        this,
        UpdateUserManageable.default()
      );
    }
  }

  required(): any {
    return {
      firstName: true,
      lastName: true,
      cellphone: true,
      birthDate: true,
      birthCity: true,
      gender: true,
      citizenship: true,
      taxNumber: true,
      notUSTax: true,
      politicallyExposed: true,
      idDocumentType: true,
      idDocumentNumber: true,
      idDocumentCountry: true,
      passportIssuingAgency: true,
      address: true,
      addressCity: true,
      addressZipcode: true,
      addressCountry: true,
      employment: true,
      prevEmployment: true,
      highestEducation: true,
      occupationGroup: true,
      experience: Object.values(ExperienceType).map((x) => ({
        level: true,
        type: true,
        transactionsPerYear: this.hasExperience(x),
        volumePerYear: this.hasExperience(x),
      })),
    };
  }

  static default(): any {
    return { ...new UpdateUserInput() };
  }

  hasExperience(type: ExperienceType) {
    return UserManageable.hasExperience(
      this,
      type
    );
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    let errors: any = {};

    errors = Object.assign(
      errors,
      validateCellphones(new CustomValidationArguments({
        keys: ['cellphone'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.INVALID]
      }))
    );

    validateRequiredNotDefault(
      errors,
      this.required(),
      this,
      {
        ...CreateUserManageable.default(), // Use defaults from CreateUserManageable since defaults from UpdateUserManageable are empty for the purpose of disabling non-editable fields
        experience: Object.values(ExperienceType).map(() => ({ })),
      },
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    return errors;
  }

  serialize(initialValues: UserManageable): any {
    let data = Object.entries(this).reduce(
      (
        result: any, [key, value]
      ) => {
        if (JSON.stringify(value) !== JSON.stringify(initialValues[key])) {
          result[key] = value;
        }
        return result;
      },
      {}
    )

    if (data.highestEducation) {
      data.highestEducation = data.highestEducation.toUpperCase();
    }

    if (data.occupationGroup) {
      data.occupationGroup = data.occupationGroup.toUpperCase();
    }

    if (data.experience) {
      data.experience = data.experience.map((experience: Experience) => Object.assign(
        experience,
        {
          type: experience.type.toUpperCase(),
          level: experience.level.toUpperCase(),
        }
      ));
    }

    return data;
  }
}